/**
 * Soft!=Live Page208 — Settings Sites residual liquid (Page150/109/59 + dual).
 * Mount: /settings/sites
 * Buses (contract ops — never painted in operator DOM).
 * Soft residual deleted:
 *  - invent seat chips while posture loading
 *  - invent site rows when site authority is off
 *  - bootstrap/grant/revoke without write seat
 *  - SA membership invent
 *  - zones filler when survey sealed
 *  - path /operation labels painted in DOM
 * Marker: Page208 Soft residual
 */
import { useEffect, useState } from "react";
import { html, F, Ico, Card, Field, Input, Alert, KV } from "./ui.js";
import { path, useAction, useApi } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { useSession } from "./session.js";
import { confirmLeaveSeat } from "./shell-wayfind.js";

const ROLES = ["site_operator", "site_viewer", "tenant_owner", "site_admin"];
const ROLE_LABEL = {
  site_operator: "Site operator",
  site_viewer: "Site viewer",
  tenant_owner: "Organisation owner",
  site_admin: "Site admin",
};

/** Path-free operator copy for Sites desk. */
export function softSitesSettingsErrorOf(err) {
  const n = String(err == null ? "" : err).trim();
  const low = n.toLowerCase();
  if (!low) return "That action could not be completed.";
  if (/\b403\b|forbidden|seat_gated|not_authorized|denied|insufficient|admin required/.test(low)) {
    return "Your account cannot manage sites here. Ask an organisation admin if you need access.";
  }
  if (/\b401\b|unauthorized|missing bearer|session/.test(low)) {
    return "Sign in again to continue.";
  }
  if (/\b422\b|\b400\b|invalid|required|membership|username|site/.test(low)) {
    return "That site change was not accepted. Check the fields and try again.";
  }
  if (/\b429\b|too many/.test(low)) {
    return "Too many tries — wait a moment, then retry.";
  }
  if (/\b503\b|unavailable|disabled|site_authority/.test(low)) {
    return "Site teams are not ready right now.";
  }
  if (/network|failed to fetch|timeout/.test(low)) {
    return "Network issue — try again in a moment.";
  }
  if (/\/api\/|\/resource-fabric|\/settings\/sites|\/zones|GET |POST |DELETE |status_path|http/.test(low)) {
    return "That action could not be completed.";
  }
  if (n.length > 160) return n.slice(0, 157) + "…";
  return n;
}

function suggestSiteId(label) {
  let s = String(label || "").trim().toLowerCase();
  s = s.replace(/[^a-z0-9._-]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 63);
  if (!s || !/^[a-z0-9]/.test(s)) return "";
  return s;
}

/** Soft≠Live Page208: missing contract ops must not crash the desk. */
function safeSitesPath(op, opts) {
  try {
    return path(op, opts);
  } catch {
    if (op === "get__settings_sites_status") return "/settings/sites/status";
    if (op === "get__resource-fabric_site-authority_status") {
      return "/resource-fabric/site-authority/status";
    }
    if (op === "get__resource-fabric_sites") return "/resource-fabric/sites";
    if (op === "get__zones") return "/zones";
    if (op === "post__resource-fabric_sites") return "/resource-fabric/sites";
    if (op === "post__resource-fabric_sites_bootstrap") {
      return "/resource-fabric/sites/bootstrap";
    }
    if (op === "get__resource-fabric_sites_p_memberships") {
      const id = opts && opts.pathParams && opts.pathParams[0];
      return id
        ? "/resource-fabric/sites/" + encodeURIComponent(id) + "/memberships"
        : "";
    }
    if (op === "post__resource-fabric_sites_p_memberships") {
      const id = opts && opts.pathParams && opts.pathParams[0];
      return id
        ? "/resource-fabric/sites/" + encodeURIComponent(id) + "/memberships"
        : "";
    }
    if (op === "delete__resource-fabric_sites_p_memberships_p") {
      const a = opts && opts.pathParams;
      if (a && a[0] && a[1]) {
        return (
          "/resource-fabric/sites/" +
          encodeURIComponent(a[0]) +
          "/memberships/" +
          encodeURIComponent(a[1])
        );
      }
      return "";
    }
    return "";
  }
}

/** Pure: seat chips from Live Settings Sites posture (Page208). */
export function sitesSettingsSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const sMax = Number(lim.sites_paint_max);
  const zMax = Number(lim.zones_paint_max);
  const mMax = Number(lim.members_paint_max);
  return {
    enabled: p.enabled === true,
    armed: p.armed === true,
    siteAuthorityAllowed: p.site_authority_allowed === true,
    siteWriteAllowed: p.site_write_allowed === true,
    membershipWriteAllowed: p.membership_write_allowed === true,
    zonesAllowed: p.zones_survey_allowed === true,
    saBlocked: p.sa_not_exposed === true,
    reReadAllowed: p.re_read_allowed === true,
    role: String(p.principal_role || ""),
    detail: p.detail || null,
    postureLabel: String(p.posture_label || "").trim() || null,
    softTitle: String(p.soft_title || "").trim() || null,
    softMessage: String(p.soft_message || "").trim() || null,
    forbidApiPaint: p.soft_api_path_paint_forbidden !== false,
    sitesPaintMax: Number.isFinite(sMax) && sMax > 0 ? Math.floor(sMax) : 40,
    zonesPaintMax: Number.isFinite(zMax) && zMax > 0 ? Math.floor(zMax) : 80,
    membersPaintMax: Number.isFinite(mMax) && mMax > 0 ? Math.floor(mMax) : 40,
  };
}

/** Pure: KPI strip for sites + zones posture. */
export function sitesDeskKpisOf(sitesPack, zonesPack, opts) {
  const sites = (sitesPack && sitesPack.items) || [];
  const zones = (zonesPack && (zonesPack.items || zonesPack.zones))
    || (Array.isArray(zonesPack) ? zonesPack : []);
  const o = opts && typeof opts === "object" ? opts : {};
  return {
    sites: sites.length,
    zones: zones.length,
    armed: !!o.armed,
    saBlocked: !!o.saBlocked,
    statusKnown: !!o.statusKnown,
    writeOk: !!o.writeOk,
  };
}

export function SitesSettingsDesk({
  toast,
  showZones = true,
  context = "settings",
  selectedSiteId = "",
  onSelectSite,
}) {
  const { me } = useSession();
  const role = String((me && me.role) || "").toLowerCase();
  const isUser = role === "user";
  const isSaStaff = role === "sa_staff";
  const isSa = role === "superadmin" || role === "sa_staff";

  const posture = useApi(safeSitesPath("get__settings_sites_status"));
  const post = posture.data || {};
  // Fail-closed while loading — seats fail-closed.
  const seat = sitesSettingsSeatOf(posture.loading || !posture.data ? {} : post);

  const zones = useApi(showZones && seat.zonesAllowed ? safeSitesPath("get__zones") : null);
  const sites = useApi(
    seat.siteAuthorityAllowed ? safeSitesPath("get__resource-fabric_sites") : null,
  );
  const siteItems = ((sites.data && sites.data.items) || []).slice(0, seat.sitesPaintMax);
  const [localSiteId, setLocalSiteId] = useState("");
  const siteId = String(selectedSiteId || localSiteId || "").trim();
  const selectSite = (id) => {
    setLocalSiteId(id);
    onSelectSite && onSelectSite(id);
  };
  const [who, setWho] = useState("");
  const [rolePick, setRolePick] = useState("site_operator");
  const [bootReason, setBootReason] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newId, setNewId] = useState("");
  const [newReason, setNewReason] = useState("");
  const [idTouched, setIdTouched] = useState(false);
  const act = useAction();
  const envelope = act.envelope;

  useEffect(() => {
    if (siteId) return;
    const first = siteItems[0];
    if (first) selectSite(String(first.site_id || first.id || "").trim());
  }, [siteItems, siteId]);

  const membPath = seat.siteAuthorityAllowed && siteId
    ? safeSitesPath("get__resource-fabric_sites_p_memberships", {
        pathParams: [siteId],
      })
    : null;
  const memb = useApi(membPath);
  const members = ((memb.data && memb.data.items) || []).slice(0, seat.membersPaintMax);
  const zoneItems = (
    (zones.data && (zones.data.items || zones.data.zones))
    || (Array.isArray(zones.data) ? zones.data : [])
  ).slice(0, seat.zonesPaintMax);

  const unarmed = !seat.enabled;
  const writeOk = seat.siteWriteAllowed && seat.membershipWriteAllowed;
  const kpis = sitesDeskKpisOf(sites.data, zones.data, {
    armed: seat.enabled || seat.armed,
    saBlocked: seat.saBlocked,
    statusKnown: !!posture.data || !!posture.error,
    writeOk,
  });

  const softTitle = seat.softTitle || seat.postureLabel || "Sites";
  const softMessage = seat.softMessage
    || seat.postureLabel
    || "Organisation Sites only — geometry lives under Ops Areas Boundary.";

  const reload = () => {
    if (!seat.reReadAllowed) {
      toast && toast("Refresh is not available for this account", "warn");
      return;
    }
    posture.reload && posture.reload();
    sites.reload && sites.reload();
    zones.reload && zones.reload();
    memb.reload && memb.reload();
    toast && toast("Sites refreshed", "go");
  };

  const createSite = async () => {
    if (!seat.siteWriteAllowed) {
      toast && toast("Only organisation admins can add a site", "warn");
      return;
    }
    const label = String(newLabel || "").trim();
    const sid = String(newId || suggestSiteId(label) || "").trim();
    const reason = String(newReason || "").trim();
    if (!label || !sid || !reason) {
      toast && toast("Name, site id, and a reason are required", "warn");
      return;
    }
    if (!/^[A-Za-z0-9][A-Za-z0-9._-]{0,62}$/.test(sid)) {
      toast && toast("Site id must start with a letter or number", "warn");
      return;
    }
    const out = await act.run(safeSitesPath("post__resource-fabric_sites"), {
      method: "POST",
      body: { site_id: sid, label, operator_reason: reason, make_default: false },
    });
    if (out) {
      toast && toast("Site added", "go");
      setNewLabel("");
      setNewId("");
      setNewReason("");
      setIdTouched(false);
      sites.reload && sites.reload();
      selectSite(String((out && out.site_id) || sid));
    } else toast && toast(softSitesSettingsErrorOf(act.error) || "Could not add the site", "bad");
  };

  const bootstrap = async () => {
    if (!seat.siteWriteAllowed) {
      toast("Only organisation admins can create the first site", "warn");
      return;
    }
    const reason = String(bootReason || "").trim() || "First site for organisation";
    const out = await act.run(safeSitesPath("post__resource-fabric_sites_bootstrap"), {
      method: "POST",
      body: { operator_reason: reason },
    });
    if (out) {
      toast("Default site created", "go");
      setBootReason("");
      sites.reload && sites.reload();
      posture.reload && posture.reload();
    } else toast(softSitesSettingsErrorOf(act.error) || "Could not create the site", "bad");
  };

  const grant = async () => {
    if (!seat.membershipWriteAllowed) {
      toast("Only organisation admins can grant site roles", "warn");
      return;
    }
    if (!siteId || !who.trim()) {
      toast("Choose a site and enter a username", "bad");
      return;
    }
    const out = await act.run(
      safeSitesPath("post__resource-fabric_sites_p_memberships", {
        pathParams: [siteId],
      }),
      {
        method: "POST",
        body: {
          principal_username: who.trim(),
          membership_role: rolePick || "site_operator",
          operator_reason: "Site membership grant from Settings",
        },
      },
    );
    if (out) {
      toast("Access granted for " + who.trim(), "go");
      setWho("");
      memb.reload && memb.reload();
    } else toast(softSitesSettingsErrorOf(act.error) || "Could not grant access", "bad");
  };

  const revoke = async (username) => {
    if (!seat.membershipWriteAllowed) {
      toast("Only organisation admins can remove site roles", "warn");
      return;
    }
    if (!siteId || !username) return;
    if (!window.confirm("Remove " + username + " from this site?")) return;
    const out = await act.run(
      safeSitesPath("delete__resource-fabric_sites_p_memberships_p", {
        pathParams: [siteId, username],
      }),
      {
        method: "DELETE",
        body: { operator_reason: "Site membership revoke from Settings" },
      },
    );
    if (out) {
      toast("Access removed for " + username, "warn");
      memb.reload && memb.reload();
    } else toast(softSitesSettingsErrorOf(act.error) || "Could not remove access", "bad");
  };

  const postureChip = posture.loading && !posture.data
    ? "…"
    : posture.error
      ? "error"
      : seat.enabled
        ? "on"
        : unarmed
          ? "off"
          : "…";

  const seatWriteLabel = writeOk
    ? "Admin"
    : seat.saBlocked
      ? "Staff limited"
      : isUser
        ? "Member"
        : isSaStaff
          ? "Staff"
          : "—";

  const nest = context === "places";

  return html`<div className="sites-set-desk sites-set-desk--liquid"
    data-cmc-page="page109-sites" data-cmc-sites="page59"
    data-cmc-residual="page208-sites" data-cmc-sites150="page150-sites"
    data-context=${context}>
    ${!nest ? html`<div className="sites-set-desk__rail">
      <div>
        <p className="eyebrow">Settings · Sites</p>
        <h2 className="sites-set-desk__title">${softTitle}</h2>
        <p className="note">${softMessage}</p>
      </div>
      <div className="btn-row sites-set-desk__acts">
        <button type="button" className="btn btn--ghost btn--sm"
          disabled=${act.busy || !seat.reReadAllowed}
          onClick=${reload}>
          <${Ico} d="refresh" w=${14}/> Refresh
        </button>
        ${showZones ? html`<button type="button" className="btn btn--ghost btn--sm"
          onClick=${()=>confirmLeaveSeat({ fromRoute: "/settings/sites", targetPath: "/places/zones" })}
          title="Open Places · Zones">
          Open Zones
        </button>` : null}
        ${isSa ? html`<button type="button" className="btn btn--ghost btn--sm"
          onClick=${()=>confirmLeaveSeat({ fromRoute: "/settings/sites", targetPath: "/sa" })}
          title="Leaving Settings · Staff console">
          Staff console
        </button>` : null}
      </div>
    </div>` : html`<div className="sites-set-desk__nest-acts btn-row">
      <button type="button" className="btn btn--ghost btn--sm"
        disabled=${act.busy || !seat.reReadAllowed}
        onClick=${reload}>
        <${Ico} d="refresh" w=${14}/> Refresh people
      </button>
    </div>`}

    ${!nest ? html`<${Alert} tone="info" title="What you can do here"
      say=${`${seat.siteAuthorityAllowed ? "Manage Sites and memberships" : "Site management sealed"}${showZones ? " · Zone geometry lives in Places" : ""}`}/>` : null}
    ${envelope ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>` : null}

    ${!nest ? html`<div className="sites-set-desk__kpis" role="group" aria-label="Sites overview">
      <div className="sites-set-desk__kpi" data-on=${kpis.armed ? "1" : "0"}>
        <span>Site teams</span><b>${postureChip}</b><em>system</em>
      </div>
      <div className="sites-set-desk__kpi" data-on=${seat.siteAuthorityAllowed && kpis.sites ? "1" : "0"}>
        <span>Sites</span>
        <b>${seat.siteAuthorityAllowed && sites.data && !sites.error ? kpis.sites : "—"}</b>
        <em>catalogue</em>
      </div>
      <div className="sites-set-desk__kpi" data-on=${seat.zonesAllowed && kpis.zones ? "1" : "0"}>
        <span>Zones</span>
        <b>${!seat.zonesAllowed || zones.error ? "—" : kpis.zones}</b>
        <em>map</em>
      </div>
      <div className="sites-set-desk__kpi" data-on=${writeOk ? "1" : "0"}>
        <span>Your seat</span>
        <b>${seatWriteLabel}</b>
        <em>membership</em>
      </div>
    </div>` : null}

    ${posture.error ? html`
      <${Alert} tone="warn" title="Could not load site settings"
        say=${softSitesSettingsErrorOf(posture.error)}/>` : null}

    ${unarmed && !posture.loading ? html`
      <${Alert} tone="warn" title=${softTitle}
        say=${softMessage}/>` : null}

    ${seat.saBlocked && !writeOk && !unarmed ? html`
      <${Alert} tone="info" title="Staff seat"
        say=${softMessage}/>` : null}

    ${!seat.siteAuthorityAllowed && !seat.saBlocked && !unarmed && (isUser || isSaStaff) ? html`
      <${Alert} tone="info" title="Site management sealed"
        say=${softMessage}/>` : null}

    ${writeOk && seat.siteAuthorityAllowed ? html`<${F}>
      <${Card} title=${nest ? "Works" : "Organisation sites"} meta=${nest ? "bootstrap · membership" : "catalogue"}>
        ${sites.error
          ? html`<${Alert} tone="warn" title="Could not load sites" say=${softSitesSettingsErrorOf(sites.error)}/>`
          : html`<div className="rows">
          ${!siteItems.length ? html`<p className="note">No sites yet — create a default site for your organisation.</p>` : null}
          ${siteItems.map((s) => {
            const id = String(s.site_id || s.id || "").trim();
            const short = id.length > 12 ? id.slice(0, 8) + "…" : id;
            return html`<button key=${id} type="button" className="row" data-on=${siteId===id?"1":"0"}
              onClick=${()=>selectSite(id)}>
              <span className="st"/><span className="main">
                <b>${s.label || s.name || short}</b>
                <span>${s.status || "active"} · ${ROLE_LABEL[s.membership_role] || s.membership_role || "member"}</span>
              </span></button>`;
          })}
        </div>`}
        ${!siteItems.length && !sites.error ? html`
          <div className="grid g2" style=${{ marginTop: 12 }}>
            <${Field} label="Reason for creating the first site"><${Input} icon="edit" value=${bootReason}
              onChange=${(e)=>setBootReason(e.target.value)}
              placeholder="e.g. primary campus"/><//>
          </div>
          <button type="button" className="btn btn--go btn--sm" style=${{ marginTop: 10 }}
            disabled=${act.busy || !seat.siteWriteAllowed} onClick=${bootstrap}>
            ${act.busy ? "Saving…" : "Create default site"}
          </button>` : null}
        ${siteItems.length && !sites.error ? html`
          <div className="grid g2" style=${{ marginTop: 12 }} data-cmc-add-site="1">
            <${Field} label="New site name"><${Input} icon="edit" value=${newLabel}
              onChange=${(e) => {
                const v = e.target.value;
                setNewLabel(v);
                if (!idTouched) setNewId(suggestSiteId(v));
              }}
              placeholder="Sibu"/><//>
            <${Field} label="Site id"><${Input} icon="edit" value=${newId}
              onChange=${(e) => { setIdTouched(true); setNewId(e.target.value); }}
              placeholder="sibu"/><//>
            <${Field} label="Reason"><${Input} icon="edit" value=${newReason}
              onChange=${(e) => setNewReason(e.target.value)}
              placeholder="Second work area"/><//>
          </div>
          <button type="button" className="btn btn--go btn--sm" style=${{ marginTop: 10 }}
            data-cmc-add-site-go="1"
            disabled=${act.busy || !seat.siteWriteAllowed || !String(newLabel).trim() || !String(newReason).trim()}
            onClick=${createSite}>
            ${act.busy ? "Saving…" : "Add site"}
          </button>` : null}
      <//>

      <${Card} title="People on this site" meta=${siteId ? "selected site" : "pick a site"} cls="mt">
        ${!siteId ? html`<p className="note">Select a site first.</p>` : html`<${F}>
          ${memb.loading
            ? html`<p className="note">Loading people…</p>`
            : memb.error
              ? html`<${Alert} tone="warn" title="Could not load memberships"
                  say=${softSitesSettingsErrorOf(memb.error)}/>`
              : html`<div className="rows">
                  ${!members.length ? html`<p className="note">No people on this site yet.</p>` : null}
                  ${members.map((m) => html`
                    <div key=${m.membership_id || m.principal_username} className="row row--say">
                      <${Ico} d="users" w=${14}/>
                      <span className="main">
                        <b>${m.principal_username}</b>
                        <span>${ROLE_LABEL[m.membership_role] || m.membership_role || "—"} · ${m.expires_at || "no expiry"}</span>
                      </span>
                      <button type="button" className="btn btn--text btn--sm"
                        disabled=${act.busy || !seat.membershipWriteAllowed}
                        onClick=${()=>revoke(m.principal_username)}>Remove</button>
                    </div>`)}
                </div>`}
          <div className="grid g2" style=${{ marginTop: 12 }}>
            <${Field} label="Username"><${Input} icon="user" value=${who}
              onChange=${(e)=>setWho(e.target.value)} placeholder="colleague username"/><//>
            <${Field} label="Role">
              <select className="inp" value=${rolePick} onChange=${(e)=>setRolePick(e.target.value)}>
                ${ROLES.map((r) => html`<option key=${r} value=${r}>${ROLE_LABEL[r] || r}</option>`)}
              </select>
            <//>
          </div>
          <button type="button" className="btn btn--go btn--sm" style=${{ marginTop: 10 }}
            disabled=${act.busy || !who.trim() || !seat.membershipWriteAllowed} onClick=${grant}>
            ${act.busy ? "Saving…" : "Grant access"}
          </button>
        <//>`}
      <//>
    <//>` : null}

    ${!showZones ? null : seat.zonesAllowed ? html`
    <${Card} title="Areas (read-only here)" meta=${zoneItems.length ? zoneItems.length + " shapes" : "geometry"} data-cmc-set="set-1">
      ${zones.loading
        ? html`<p className="note">Loading areas…</p>`
        : zones.error
          ? html`<${Alert} tone="warn" title="Could not load areas" say=${softSitesSettingsErrorOf(zones.error)}/>`
          : html`<div className="rows">
              ${!zoneItems.length ? html`<p className="note">No areas for this account yet.</p>` : null}
              ${zoneItems.map((z) => html`
                <div key=${z.id || z.space_id || z.name} className="row row--say">
                  <${Ico} d="map" w=${14}/>
                  <span className="main">
                    <b>${z.label || z.name || z.id || "area"}</b>
                    <span>${z.kind || z.type || "polygon"} · ${z.device_count != null ? z.device_count + " devices" : "geometry"}</span>
                  </span>
                </div>`)}
            </div>`}
      <p className="note">Sites own organisation membership. Edit Area Focus / Boundary under Ops Areas.</p>
      <div className="btn-row" style=${{ marginTop: 8 }}>
        <button type="button" className="btn btn--go btn--sm"
          onClick=${()=>confirmLeaveSeat({ fromRoute: "/settings/sites", targetPath: "/places/zones" })}>
          Open Zones</button>
      </div>
    <//>` : html`
    <${Alert} tone="info" title="Areas catalogue sealed for this session"
      say="Area shapes stay sealed until the server opens the survey seat."/>`}
  </div>`;
}

export default SitesSettingsDesk;
